import type { QuoteDocument, QuoteLineItem, QuoteStatus, ValidityDays } from '../types'
import { calcTotals, createEmptyLine, createLineFromProduct, recalcLine } from '../utils/pricing'
import { createFakeQuotes, nextQuoteCode } from '../utils/mock-quotes'
import { snapshotEngine } from '@/services/snapshot'
import { storageService } from '@/services/storage'

const STORAGE_KEY = 'masterhub.quotes.v1'

/**
 * QuoteRepository — localStorage persistence mirroring Prisma Quote/QuoteItem.
 */
export class QuoteRepository {
  private memory: QuoteDocument[] | null = null

  private ensureSeed(): QuoteDocument[] {
    const existing = this.readAll()
    if (existing.length > 0) return existing
    const seeded = createFakeQuotes(50)
    this.writeAll(seeded)
    return seeded
  }

  listAll(): QuoteDocument[] {
    return this.ensureSeed()
      .slice()
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }

  findById(id: string): QuoteDocument | null {
    return this.listAll().find((q) => q.id === id) ?? null
  }

  createDraft(): QuoteDocument {
    const now = new Date().toISOString()
    const all = this.listAll()
    const doc: QuoteDocument = {
      id: crypto.randomUUID(),
      code: nextQuoteCode(all),
      title: 'Novo orçamento',
      status: 'DRAFT',
      customerId: null,
      customerName: null,
      validUntilDays: 7,
      quoteDate: now,
      discountPercent: 0,
      discountValue: 0,
      freight: 0,
      notes: '',
      items: [],
      totals: calcTotals([], 0, 0, 0),
      snapshotJson: null,
      pdfUrl: null,
      createdAt: now,
      updatedAt: now,
    }
    this.writeAll([doc, ...all])
    return doc
  }

  save(doc: QuoteDocument): QuoteDocument {
    const totals = calcTotals(
      doc.items,
      doc.discountPercent,
      doc.discountValue,
      doc.freight,
    )
    const next: QuoteDocument = {
      ...doc,
      items: doc.items.map((item, index) => ({ ...item, sortOrder: index })),
      totals,
      updatedAt: new Date().toISOString(),
    }

    const all = this.listAll()
    const index = all.findIndex((q) => q.id === next.id)
    if (index < 0) {
      this.writeAll([next, ...all])
    } else {
      all[index] = next
      this.writeAll(all)
    }
    return next
  }

  duplicate(id: string): QuoteDocument {
    const source = this.findById(id)
    if (!source) throw new Error('Orçamento não encontrado')
    const now = new Date().toISOString()
    const copy: QuoteDocument = {
      ...structuredClone(source),
      id: crypto.randomUUID(),
      code: nextQuoteCode(this.listAll()),
      title: `${source.title ?? source.code} (cópia)`,
      status: 'DRAFT',
      createdAt: now,
      updatedAt: now,
      pdfUrl: null,
      snapshotJson: null,
      items: source.items.map((item) => ({
        ...item,
        id: crypto.randomUUID(),
      })),
    }
    return this.save(copy)
  }

  remove(id: string): void {
    this.writeAll(this.listAll().filter((q) => q.id !== id))
  }

  clearItems(id: string): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    return this.save({ ...doc, items: [] })
  }

  addManualItem(
    id: string,
    description: string,
    quantity: number,
    salePrice: number,
  ): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const purchase = salePrice * 0.7
    const base = createEmptyLine(doc.items.length)
    const item = recalcLine(
      {
        ...base,
        description,
        quantity,
        purchasePrice: purchase,
        operational: salePrice * 0.05,
        taxes: salePrice * 0.08,
        profit: Math.max(0, salePrice - purchase - salePrice * 0.13),
        salePrice,
      },
      'salePrice',
    )
    return this.save({ ...doc, items: [...doc.items, item] })
  }

  addProductLine(
    id: string,
    product: { id: string; descricao: string; valorManual: number },
  ): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const existing = doc.items.find((i) => i.productId === product.id)
    if (existing) {
      const items = doc.items.map((i) =>
        i.id === existing.id
          ? recalcLine({ ...i, quantity: i.quantity + 1 }, 'components')
          : i,
      )
      return this.save({ ...doc, items })
    }
    const line = createLineFromProduct({
      productId: product.id,
      description: product.descricao,
      purchasePrice: product.valorManual * 0.75,
      salePrice: product.valorManual,
      sortOrder: doc.items.length,
    })
    return this.save({ ...doc, items: [...doc.items, line] })
  }

  updateLine(
    id: string,
    lineId: string,
    patch: Partial<QuoteLineItem>,
    mode: 'components' | 'salePrice' | 'purchase' | 'profit' = 'components',
  ): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const items = doc.items.map((item) => {
      if (item.id !== lineId) return item
      return recalcLine({ ...item, ...patch }, mode)
    })
    return this.save({ ...doc, items })
  }

  removeLine(id: string, lineId: string): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    return this.save({ ...doc, items: doc.items.filter((i) => i.id !== lineId) })
  }

  duplicateLine(id: string, lineId: string): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const source = doc.items.find((i) => i.id === lineId)
    if (!source) throw new Error('Item não encontrado')
    const copy = { ...source, id: crypto.randomUUID(), sortOrder: doc.items.length }
    return this.save({ ...doc, items: [...doc.items, copy] })
  }

  reorderLines(id: string, fromIndex: number, toIndex: number): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const items = [...doc.items]
    const [moved] = items.splice(fromIndex, 1)
    if (!moved) return doc
    items.splice(toIndex, 0, moved)
    return this.save({ ...doc, items })
  }

  updateMeta(
    id: string,
    patch: Partial<
      Pick<
        QuoteDocument,
        | 'customerId'
        | 'customerName'
        | 'status'
        | 'validUntilDays'
        | 'discountPercent'
        | 'discountValue'
        | 'freight'
        | 'notes'
        | 'title'
        | 'quoteDate'
      >
    >,
  ): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    return this.save({ ...doc, ...patch })
  }

  /** Snapshot Engine — captures immutable photograph and stores reference. */
  prepareSnapshot(id: string): QuoteDocument {
    const doc = this.findById(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const snapshot =
      snapshotEngine.captureIfChanged(doc) ??
      snapshotEngine.getLatest(doc.id) ??
      snapshotEngine.createFromQuote(doc)
    return this.save({
      ...doc,
      snapshotJson: JSON.stringify({
        engine: 'snapshot-engine',
        snapshotId: snapshot.id,
        version: snapshot.version,
        versionLabel: snapshot.versionLabel,
        capturedAt: new Date().toISOString(),
        quoteId: doc.id,
      }),
    })
  }

  resetMemory(): void {
    this.memory = null
  }

  private readAll(): QuoteDocument[] {
    if (typeof window === 'undefined') return this.memory ?? []
    return storageService.getJSON<QuoteDocument[]>(STORAGE_KEY, [])
  }

  private writeAll(quotes: QuoteDocument[]): void {
    if (typeof window === 'undefined') {
      this.memory = quotes
      return
    }
    storageService.setJSON(STORAGE_KEY, quotes)
  }
}

export const quoteRepository = new QuoteRepository()

export type { QuoteStatus, ValidityDays }
